import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box } from '@mui/material';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import React from 'react';
import { useSelector } from 'react-redux';
import Button from '/@/components/button';
import Drawing from '/@/components/drawing';
import Dropdown from '/@/components/dropdown';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import { getLibraryEnabled } from '/@/store/library/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';

/** @param {{onNewRecomendation: VoidFunction}} props */
export function NigrogenListHeader(props) {
    return (
        <HeaderContainer>
            <Grid container spacing={2} justifyContent='space-between' alignItems='center'>
                <Grid item xs={12} sm sx={{ '& > * + *': { mt: 1 }, flexGrow: { sm: 1 } }}>
                    <Title size={!props.hideButtons ? 'xxl' : 'lg'} color={!props.hideButtons && 'white'} bold>
                        {_('Nitrógeno N-Smart')}
                    </Title>
                    <Title color={!props.hideButtons && 'white'} size='md'>
                        {_(
                            'Recomendación integrada de fertilización para mejorar la eficiencia en el uso de nitrógeno en maíz'
                        )}
                    </Title>
                </Grid>
                {!props.hideButtons && (
                    <Grid item xs={12} sm container spacing={1} justifyContent='flex-end' alignItems='center'>
                        <Grid item xs={12} sm='auto'>
                            <KnowMoreDropdownButton props={props} />
                        </Grid>
                        <Grid item xs={12} sm='auto'>
                            <Button
                                fullWidth
                                onClick={props.onNewRecomendation}
                                startIcon={<AddIcon />}
                                color='white'
                                disableElevation
                                variant='contained'
                                sx={{ color: (t) => t.palette.primary.main }}
                            >
                                {_('Nueva recomendación')}
                            </Button>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </HeaderContainer>
    );
}

export const NigrogenNewHeader = React.forwardRef<HTMLDivElement, { handleReset: () => void }>(
    function NigrogenNewHeader({ handleReset }, ref) {
        const { open: openDialog, toggle } = useToggle();

        return (
            <HeaderContainer ref={ref}>
                <Grid container spacing={2} justifyContent='space-between' alignItems='center'>
                    <Grid item xs={12} sm sx={{ '& > * + *': { mt: 1 }, flexGrow: { sm: 1 } }}>
                        <Title size='xxl' color='white' bold>
                            {_('Nueva recomendación N-Smart')}
                        </Title>
                        <Title color='white' size='md'>
                            <span style={{ display: 'flex' }}>
                                <WarningAmberRoundedIcon htmlColor='yellow' sx={{ verticalAlign: 'sub' }} />
                                &nbsp;
                                {_('Programa sujeto a disponibilidad técnica y geográfica')}.
                            </span>
                        </Title>
                    </Grid>
                    <Grid item xs={12} sm container spacing={1} justifyContent='flex-end' alignItems='center'>
                        <Grid item xs={12} sm='auto'>
                            <KnowMoreDropdownButton />
                        </Grid>
                        <Grid item xs={12} sm='auto'>
                            <Button
                                fullWidth
                                onClick={toggle}
                                color='white'
                                disableElevation
                                variant='contained'
                                sx={{ color: (t) => t.palette.primary.main }}
                            >
                                {_('Menú N-Smart')}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Dialog open={openDialog} maxWidth='xs' fullWidth onClose={toggle}>
                    <DialogContent
                        sx={{
                            display: 'flex',
                            position: 'relative',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            rowGap: 1,
                        }}
                    >
                        <IconButton onClick={toggle} sx={{ position: 'absolute', right: '10px', top: '10px' }}>
                            <CloseIcon />
                        </IconButton>
                        <Drawing
                            size='sm'
                            type='warning-corn'
                            title={_('Importante')}
                            subtitle={
                                <React.Fragment>
                                    {_('Estás a punto de salir de Recomendación N-Smart. Se ')}
                                    <Box component='span' sx={{ color: (t) => t.palette.error.main }}>
                                        {_('eliminará')}
                                    </Box>{' '}
                                    {_(' la recomendación junto a todos los datos que cargaste')}
                                </React.Fragment>
                            }
                        />
                        <Button color='primary' variant='contained' disableElevation fullWidth onClick={toggle}>
                            {_('Quedarme en recomendación N-Smart')}
                        </Button>
                        <Button
                            color='error'
                            variant='outlined'
                            disableElevation
                            fullWidth
                            onClick={() => {
                                handleReset();
                            }}
                        >
                            {_('Salir y eliminar todos los datos cargados')}
                        </Button>
                    </DialogContent>
                </Dialog>
            </HeaderContainer>
        );
    }
);

const HeaderContainer = React.forwardRef<HTMLDivElement, { children: React.ReactNode }>(function HeaderContainer(
    { children },
    ref
) {
    return (
        <Box sx={{ bgcolor: (t) => ({ xs: 'white', md: t.palette.primary.main }), py: 4 }} ref={ref}>
            <Container maxWidth='lg'>{children}</Container>
        </Box>
    );
});

function KnowMoreDropdownButton(props) {
    const libraryEnabled = useSelector(getLibraryEnabled);
    const redirect = (slug: string) => window.open(reverse('library.LibraryDetail', { params: { slug } }));
    if (!libraryEnabled) return null;
    return (
        <React.Fragment>
            <Dropdown label={_('Quiero saber más')} ButtonProps={{ fullWidth: true }}>
                <MenuItem
                    onClick={() => {
                        mpTrackEvent('User wants to know more about nutrients', {
                            entry_source: 'nitrogen-recommendation-detail',
                        });
                        redirect('nitrogeno-smart-nutrientes');
                    }}
                    disableRipple
                    sx={{ color: (t) => t.palette.primary.main }}
                >
                    {_('Sobre nutrientes')}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        mpTrackEvent('User wants to know more about the N-Smart program', {
                            entry_source: 'nitrogen-recommendation-detail',
                        });
                        redirect('nitrogeno-smart-programa');
                    }}
                    disableRipple
                    sx={{ color: (t) => t.palette.primary.main }}
                >
                    {_('Sobre el programa')}
                </MenuItem>
            </Dropdown>
        </React.Fragment>
    );
}
